import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

export type StockCategory = "deal" | "event" | "business";

const STOCK_IMAGES: Record<StockCategory, { url: string; label: string }[]> = {
  deal: [
    { url: "/stock/deals/restaurant-deal.png", label: "Restaurant" },
    { url: "/stock/deals/restaurant-deal-2.png", label: "Café Special" },
    { url: "/stock/deals/restaurant-deal-3.png", label: "Family Dining" },
    { url: "/stock/deals/restaurant-deal-4.png", label: "Takeaway" },
    { url: "/stock/deals/restaurant-deal-5.png", label: "Fine Dining" },
    { url: "/stock/deals/retail-deal.png", label: "Retail" },
    { url: "/stock/deals/retail-deal-2.png", label: "Fashion" },
    { url: "/stock/deals/retail-deal-3.png", label: "Electronics" },
    { url: "/stock/deals/retail-deal-4.png", label: "Home & Garden" },
    { url: "/stock/deals/retail-deal-5.png", label: "Gifts" },
    { url: "/stock/deals/beauty-deal.png", label: "Beauty & Spa" },
    { url: "/stock/deals/beauty-deal-2.png", label: "Hair Salon" },
    { url: "/stock/deals/beauty-deal-3.png", label: "Massage" },
    { url: "/stock/deals/beauty-deal-4.png", label: "Nails" },
    { url: "/stock/deals/beauty-deal-5.png", label: "Skincare" },
    { url: "/stock/deals/fitness-deal.png", label: "Fitness" },
    { url: "/stock/deals/fitness-deal-2.png", label: "Gym" },
    { url: "/stock/deals/fitness-deal-3.png", label: "Yoga" },
    { url: "/stock/deals/fitness-deal-4.png", label: "Swimming" },
    { url: "/stock/deals/fitness-deal-5.png", label: "Sports" },
    { url: "/stock/deals/services-deal.png", label: "Home Services" },
    { url: "/stock/deals/services-deal-2.png", label: "Plumbing" },
    { url: "/stock/deals/services-deal-3.png", label: "Cleaning" },
    { url: "/stock/deals/services-deal-4.png", label: "Garden" },
    { url: "/stock/deals/services-deal-5.png", label: "Decorating" },
  ],
  event: [
    { url: "/stock/events/community-market.png", label: "Community Market" },
    { url: "/stock/events/community-market-2.png", label: "Farmers Market" },
    { url: "/stock/events/community-market-3.png", label: "Craft Fair" },
    { url: "/stock/events/community-market-4.png", label: "Street Market" },
    { url: "/stock/events/community-market-5.png", label: "Artisan Market" },
    { url: "/stock/events/live-music.png", label: "Live Music" },
    { url: "/stock/events/live-music-2.png", label: "Jazz Night" },
    { url: "/stock/events/live-music-3.png", label: "Rock Concert" },
    { url: "/stock/events/live-music-4.png", label: "Acoustic Session" },
    { url: "/stock/events/live-music-5.png", label: "Open Mic" },
    { url: "/stock/events/family-fun-day.png", label: "Family Fun Day" },
    { url: "/stock/events/family-fun-day-2.png", label: "Kids Activities" },
    { url: "/stock/events/family-fun-day-3.png", label: "Outdoor Games" },
    { url: "/stock/events/family-fun-day-4.png", label: "Picnic" },
    { url: "/stock/events/family-fun-day-5.png", label: "Theme Day" },
    { url: "/stock/events/charity-gala.png", label: "Charity Gala" },
    { url: "/stock/events/charity-gala-2.png", label: "Fundraiser" },
    { url: "/stock/events/charity-gala-3.png", label: "Quiz Night" },
    { url: "/stock/events/charity-gala-4.png", label: "Auction" },
    { url: "/stock/events/charity-gala-5.png", label: "Dinner Dance" },
    { url: "/stock/events/sports-day.png", label: "Sports Day" },
    { url: "/stock/events/sports-day-2.png", label: "Football" },
    { url: "/stock/events/sports-day-3.png", label: "Running" },
    { url: "/stock/events/sports-day-4.png", label: "Cycling" },
    { url: "/stock/events/sports-day-5.png", label: "Swimming" },
  ],
  business: [
    { url: "/stock/businesses/cafe.png", label: "Café & Coffee" },
    { url: "/stock/businesses/retail-shop.png", label: "Retail Shop" },
    { url: "/stock/businesses/tradesperson.png", label: "Tradespeople" },
    { url: "/stock/businesses/beauty-salon.png", label: "Beauty Salon" },
    { url: "/stock/businesses/professional-office.png", label: "Professional" },
  ],
};

interface Props {
  open: boolean;
  onClose: () => void;
  category: StockCategory;
  onSelect: (url: string) => void;
  selected?: string;
}

export function StockImagePicker({ open, onClose, category, onSelect, selected }: Props) {
  const images = STOCK_IMAGES[category] ?? [];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose a stock image</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">
          Pick one of our free stock images, or close this to upload your own.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
          {images.map(img => (
            <button
              key={img.url}
              type="button"
              onClick={() => { onSelect(img.url); onClose(); }}
              className={`relative group rounded-xl overflow-hidden border-2 transition-all focus:outline-none ${
                selected === img.url
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-transparent hover:border-primary/40"
              }`}
            >
              <img src={img.url} alt={img.label} className="w-full h-32 object-cover" />
              {selected === img.url && (
                <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-0.5 shadow">
                  <Check className="w-3 h-3" />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                <p className="text-white text-xs font-medium">{img.label}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end mt-1">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
